import { Button } from '@solinkcorp/solink-ui/components/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@solinkcorp/solink-ui/components/Dialog';
import { Input } from '@solinkcorp/solink-ui/components/Input';
import { showToast } from '@solinkcorp/solink-ui/components/Toast';
import { Skeleton } from '@solinkcorp/solink-ui/components/ui/skeleton';
import { SearchClearButton } from 'components/SearchClearButton';
import { Check, FolderPlus, Search, X } from 'lucide-react';
import moment from 'moment';
import { useCallback, useMemo, useState, type FC } from 'react';
import { useInvestigationEventsMutation } from 'src/hooks/investigations/useInvestigationEventsMutation';
import {
  useInvestigations,
  type Investigation,
  type InvestigationEventKind,
} from 'src/hooks/investigations/useInvestigations';
import useLocale from 'src/hooks/useLocale';
import { CreateInvestigationModal } from './CreateInvestigationModal';
import { trackInvestigationItemAdded } from './investigationAnalytics';
import { InvestigationCountBadges } from './InvestigationCountBadges';
import { InvestigationsEmptyState } from './InvestigationsEmptyState';

interface AddToInvestigationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  siteId: string;
  kind: InvestigationEventKind;
}

const PickerListSkeleton: FC = () => (
  <div className="tw flex flex-col">
    {Array.from({ length: 3 }).map((_, i) => (
      <div
        key={i}
        className="tw flex items-start justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
      >
        <div className="tw flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="tw h-4 w-2/5 max-w-56 bg-background-elevated" />
          <Skeleton className="tw h-3 w-3/5 max-w-72 bg-background-elevated" />
          <Skeleton className="tw h-5 w-24 bg-background-elevated" />
        </div>
        <Skeleton className="tw h-3 w-20 shrink-0 bg-background-elevated" />
      </div>
    ))}
  </div>
);

const InvestigationPickerRow: FC<{
  investigation: Investigation;
  added: boolean;
  onAdd: (id: string) => Promise<void>;
  dateFormat: string;
}> = ({ investigation, added, onAdd, dateFormat }) => {
  const [isAdding, setIsAdding] = useState(false);

  const handleClick = useCallback(async () => {
    if (added || isAdding) return;
    setIsAdding(true);
    try {
      await onAdd(investigation.id);
    } finally {
      setIsAdding(false);
    }
  }, [added, isAdding, onAdd, investigation.id]);

  return (
    <button
      type="button"
      onClick={() => {
        void handleClick();
      }}
      disabled={added || isAdding}
      className={`tw flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 ${
        added
          ? 'tw cursor-not-allowed opacity-60'
          : 'tw cursor-pointer hover:bg-background-elevated'
      }`}
    >
      <div className="tw flex min-w-0 flex-1 flex-col gap-1">
        <div className="tw flex items-start justify-between gap-4">
          <span className="tw truncate text-foreground-primary heading-sm">
            {investigation.title}
          </span>
          <span className="tw shrink-0 text-foreground-secondary body-md">
            {moment(investigation.updatedAt).format(dateFormat)}
          </span>
        </div>
        {investigation.description ? (
          <span className="tw line-clamp-1 text-foreground-secondary body-md">
            {investigation.description}
          </span>
        ) : null}
        <InvestigationCountBadges
          investigation={investigation}
          className="tw mt-2 flex items-center gap-2"
        />
      </div>
      {added && <Check className="tw mt-0.5 size-3.5 shrink-0 text-foreground-green" />}
    </button>
  );
};

export const AddToInvestigationModal: FC<AddToInvestigationModalProps> = ({
  open,
  onOpenChange,
  eventId,
  siteId,
  kind,
}) => {
  const locale = useLocale();
  const t = locale.investigations;
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  // Client-side filter of open investigations — same approach as mobile
  // InvestigationPickerSheet (title + description).
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { investigations, isLoading, error } = useInvestigations('open');
  const { addEventAndRevalidate } = useInvestigationEventsMutation();

  // Investigations that already contain this event (any kind) — server 409s
  // duplicates, so pre-disable those rows instead of only tracking this session.
  const alreadyContainingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const inv of investigations ?? []) {
      if (inv.events.some((item) => item.eventId === eventId)) {
        ids.add(inv.id);
      }
    }
    return ids;
  }, [investigations, eventId]);

  const filteredInvestigations = useMemo(() => {
    const list = investigations ?? [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (inv) =>
        inv.title.toLowerCase().includes(q) ||
        (inv.description?.toLowerCase().includes(q) ?? false),
    );
  }, [investigations, searchQuery]);

  const handleAdd = useCallback(
    async (investigationId: string) => {
      try {
        await addEventAndRevalidate({ investigationId, eventId, siteId, kind });
        trackInvestigationItemAdded({
          kind,
          itemId: eventId,
          investigationId,
          isNewInvestigation: false,
        });
        setAddedIds((prev) => new Set(prev).add(investigationId));
        showToast.success({ title: t.addTo.toastAdded });
      } catch (err) {
        showToast.error({
          title: err instanceof Error ? err.message : t.addTo.toastAddError,
        });
      }
    },
    [addEventAndRevalidate, eventId, siteId, kind, t.addTo],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setAddedIds(new Set());
        setSearchQuery('');
        setCreateOpen(false);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleOpenCreate = useCallback(() => {
    // Close the picker Dialog while create is open so Radix focus / Escape /
    // aria-hidden do not fight a stacked sibling dialog. Canceling create
    // reopens the picker (`open && !createOpen`).
    setCreateOpen(true);
  }, []);

  const handleCreateOpenChange = useCallback((nextOpen: boolean) => {
    setCreateOpen(nextOpen);
  }, []);

  const handleCreated = useCallback(
    async (created: { id: string }) => {
      try {
        await addEventAndRevalidate({
          investigationId: created.id,
          eventId,
          siteId,
          kind,
        });
        trackInvestigationItemAdded({
          kind,
          itemId: eventId,
          investigationId: created.id,
          isNewInvestigation: true,
        });
        setAddedIds((prev) => new Set(prev).add(created.id));
        showToast.success({ title: t.addTo.toastAdded });
        handleOpenChange(false);
      } catch (err) {
        showToast.error({
          title: err instanceof Error ? err.message : t.addTo.toastAddError,
        });
        // Keep parent open — create dialog closes next and the picker returns
        // with the new investigation available to retry.
      }
    },
    [addEventAndRevalidate, eventId, siteId, kind, t.addTo, handleOpenChange],
  );

  const trimmedSearch = searchQuery.trim();

  return (
    <>
      <Dialog open={open && !createOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="tw flex max-h-[85vh] w-full max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 text-foreground-primary sm:max-w-2xl"
        >
          {/*
            DialogHeader's -mx-6 -mt-6 assumes DialogContent p-6. This dialog is
            p-0 and overflow-hidden, so that bleed clips the title.
          */}
          <div className="tw flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
            <DialogTitle className="tw flex min-w-0 flex-1 items-center gap-2">
              <FolderPlus className="tw size-4 shrink-0" />
              <span className="tw truncate">{t.addToInvestigation}</span>
            </DialogTitle>
            <DialogClose className="tw flex size-9 shrink-0 items-center justify-center rounded-lg p-1 text-foreground-secondary transition-colors hover:bg-background-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
              <X className="tw size-4" />
              <span className="tw sr-only">{locale.general.close}</span>
            </DialogClose>
          </div>

          <div className="tw shrink-0 border-b border-border px-4 py-3">
            <Input
              placeholder={t.search.placeholderShort}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search />}
              rightIcon={
                searchQuery ? (
                  <SearchClearButton
                    label={locale.general.clear}
                    onClick={() => setSearchQuery('')}
                  />
                ) : undefined
              }
              className="tw"
            />
          </div>

          <div className="tw min-h-[280px] min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
            {isLoading && filteredInvestigations.length === 0 ? (
              <PickerListSkeleton />
            ) : error && filteredInvestigations.length === 0 ? (
              <InvestigationsEmptyState
                title={t.empty.loadErrorTitle}
                body={t.empty.loadErrorBody}
              />
            ) : filteredInvestigations.length === 0 ? (
              trimmedSearch ? (
                <InvestigationsEmptyState title={t.empty.searchTitle} body={t.empty.searchBody} />
              ) : (
                <InvestigationsEmptyState title={t.addTo.noOpen} />
              )
            ) : (
              filteredInvestigations.map((inv) => (
                <InvestigationPickerRow
                  key={inv.id}
                  investigation={inv}
                  added={addedIds.has(inv.id) || alreadyContainingIds.has(inv.id)}
                  onAdd={handleAdd}
                  dateFormat={locale.date.format1}
                />
              ))
            )}
          </div>

          <div className="tw flex shrink-0 items-center justify-between gap-2 border-t border-border px-4 py-3">
            <Button className="tw gap-2" onClick={handleOpenCreate}>
              <FolderPlus className="tw size-4" />
              {t.newInvestigation}
            </Button>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {locale.general.close}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CreateInvestigationModal
        open={createOpen}
        onOpenChange={handleCreateOpenChange}
        onCreated={handleCreated}
        source="add-to-investigation"
      />
    </>
  );
};
